import { Company } from 'module/api/company/types';
import { fullName } from 'module/helpers/full-name';
import { initials } from 'module/helpers/initials';
import { Dropdown } from 'module/ui/components/atoms/dropdown/dropdown';
import { humanise } from 'module/ui/components/atoms/humanised-date';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { CenterAlignedBox } from 'module/ui/components/atoms/layout/layout';
import {
  Heading03,
  Heading07,
  Short00,
  Short01,
} from 'module/ui/components/styles';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons';
import { faLink } from '@fortawesome/pro-regular-svg-icons';
import { faEllipsis } from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@rocketlane/ui';
import { Image as AntImage } from 'antd';
import {
  AccountPeopleProfile,
  UserExperience,
} from 'api/account-people/profile-type';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

const Image = styled(AntImage)``;
const AvatarWrapper = styled.div`
  width: 290px;
  height: 290px;
  border-radius: var(--border-radius-small);
  background: var(--gray-gradient);
  display: flex;
  align-items: center;
  justify-content: center;
  ${Heading07};
  color: var(--white-100);
  font-size: 54px;
  overflow: hidden;
  ${Image} {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UserProfileImage = ({ user }: { user: AccountPeopleProfile['user'] }) => {
  const [errored, setErrored] = useState(false);
  const fullname = fullName(user);
  const userInitials = initials(fullname);

  return (
    <AvatarWrapper>
      {user.photoUrl != null && !errored ? (
        <Image
          loading="lazy"
          alt={user.firstName ?? user.lastName}
          src={user.photoUrl}
          crossOrigin="anonymous"
          preview={false}
          onError={() => setErrored(true)}
        />
      ) : (
        <>{userInitials}</>
      )}
    </AvatarWrapper>
  );
};
const UserName = styled.div`
  ${Heading03};
`;
const PrimaryContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const RoleInfo = styled(CenterAlignedBox)`
  flex-wrap: wrap;
  row-gap: 0;
`;
const RoleName = styled.span`
  ${Short01};
`;
const Since = styled.span`
  ${Short01};
  color: var(--text-tertiary);
`;
const Location = styled.div`
  ${Short00};
  color: var(--text-tertiary);
`;
const SecondaryContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-02);
`;

const UserInformationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
`;
const UserInformation = ({
  userProfile,
}: {
  userProfile: AccountPeopleProfile;
}) => {
  const fullname = fullName(userProfile.user);
  const experience = userProfile.userProfile?.enrichedUserData?.userExperience;
  const latestExperience: UserExperience | undefined = useMemo(() => {
    return experience
      ?.sort((a, b) => {
        return (
          new Date(b.endDate ?? '').getTime() -
          new Date(a.endDate ?? '').getTime()
        );
      })
      .filter((e) => e.endDate == null)[0];
  }, [experience]);
  const title = userProfile.userProfile?.enrichedUserData?.title;
  const location = userProfile.userProfile?.enrichedUserData?.location;
  const noSecondaryContent =
    latestExperience == null && location == null && title == null;
  const copyCurrentUrlToClipboard = () => {
    if (typeof window !== 'undefined' && window.location) {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <UserInformationWrapper>
      <PrimaryContent>
        <UserName>{fullname}</UserName>
        <Dropdown
          trigger={['click']}
          menu={{
            onClick: (e) => {
              if (e.key === 'copy_url') {
                copyCurrentUrlToClipboard();
              }
            },
            items: [
              {
                type: 'group',
                label: <FormattedMessage id="more" />,
                children: [
                  {
                    key: 'linkedin',
                    label: (
                      <FormattedMessage id="account.people.profile.user_information.more_actions.linkedin" />
                    ),
                    icon: <FontAwesomeIcon icon={faLinkedin} />,
                  },
                  {
                    key: 'copy_url',
                    label: <FormattedMessage id="copy_url" />,
                    icon: <FontAwesomeIcon icon={faLink} />,
                  },
                ],
              },
            ],
          }}
        >
          <Button
            variant="ghost"
            leftIcon={<FontAwesomeIcon icon={faEllipsis} size={12} />}
          />
        </Dropdown>
      </PrimaryContent>
      {!noSecondaryContent && (
        <SecondaryContent>
          {latestExperience != null || title != null ? (
            <RoleInfo>
              <RoleName>{latestExperience?.role || title}</RoleName>
              {latestExperience != null && (
                <Since>
                  <FormattedMessage
                    id="account.people.profile.user_information.in_role_since"
                    values={{
                      date: humanise(moment(latestExperience.startDate)),
                    }}
                  />
                </Since>
              )}
            </RoleInfo>
          ) : null}
          {location != null && (
            <Location>
              <FormattedMessage
                id="account.people.profile.user_information.location"
                values={{
                  location,
                }}
              />
            </Location>
          )}
        </SecondaryContent>
      )}
    </UserInformationWrapper>
  );
};
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-05);
`;

interface Props {
  userProfile: AccountPeopleProfile;
  company: Company;
}
export const BasicInformation = ({ userProfile }: Props) => {
  const { user } = userProfile;

  return (
    <Wrapper>
      <UserProfileImage user={user} />
      <UserInformation userProfile={userProfile} />
    </Wrapper>
  );
};
